/**
 * Score-distribution histogram for the presenter results view. Hand-rolled
 * div bars (mirroring admin/activity/activity-chart.tsx) rather than a charting
 * lib — the tallest bucket carries the J&J-red emphasis, the rest recede to a
 * faint red tint. Bar heights use inline styles: the one sanctioned exception
 * to the "utility classes only" rule, shared with the activity chart.
 */

import type { ScoreBucket } from "@/lib/api";
import { cn } from "@/lib/utils";

export function VoteScoreChart({ distribution }: { distribution: ScoreBucket[] }) {
  const max = Math.max(1, ...distribution.map((bucket) => bucket.count));
  const hasVotes = distribution.some((bucket) => bucket.count > 0);
  // Index of the tallest bucket — it alone gets the red emphasis. Ties resolve
  // to the lowest score, matching a left-to-right read of the axis.
  const maxIndex = distribution.reduce(
    (best, bucket, index) => (bucket.count > (distribution[best]?.count ?? 0) ? index : best),
    0,
  );

  return (
    <div>
      <div className="flex h-40 items-stretch gap-2">
        {distribution.map((bucket, index) => {
          const emphasized = hasVotes && index === maxIndex;
          return (
            <div key={bucket.score} className="relative flex flex-1 flex-col justify-end pt-6">
              <span className="text-muted absolute inset-x-0 top-0 text-center text-xs font-medium tabular-nums">
                {bucket.count}
              </span>
              <div
                className={cn("w-full rounded-t", emphasized ? "bg-accent" : "bg-accent/20")}
                style={{ height: `${Math.max(2, (bucket.count / max) * 100)}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex gap-2">
        {distribution.map((bucket) => (
          <span key={bucket.score} className="text-muted flex-1 text-center text-xs font-medium">
            {bucket.score}
          </span>
        ))}
      </div>
    </div>
  );
}
